import type { Lang } from '../i18n/langs';
import blogImages from './blogImages.json';

export interface PostTranslation {
  title: string;
  excerpt: string;
  imageAlt: string;
  tags: string[];
  content: string;
}

type Section = [string, string[]];

// Mismo formato de texto que usa src/data/blogPosts.ts (lo convierte renderBlogContent)
const buildContent = (intro: string, sections: Section[], outro: string): string =>
  [intro, ...sections.map(([heading, items]) => [heading, ...items.map((i) => '- ' + i)].join('\n')), outro].join('\n\n');

/**
 * Traducciones de los artículos del blog (el español viene de la base de datos).
 * Clave = slug del artículo. Si un artículo no tiene traducción, se muestra en español.
 */
export const blogTranslations: Record<string, Partial<Record<Lang, PostTranslation>>> = {
  'detectar-problemas-electricos-hogar': {
    ca: {
      title: "Com detectar problemes elèctrics a casa abans que siguin greus",
      excerpt: "Aprèn a identificar els senyals d'alerta de problemes elèctrics a casa teva i evita situacions perilloses.",
      imageAlt: "Electricista revisant un quadre elèctric amb equip de seguretat",
      tags: ["seguretat elèctrica", "manteniment elèctric", "problemes elèctrics", "instal·lació elèctrica"],
      content: buildContent(
        "Els problemes elèctrics poden ser perillosos si no es detecten a temps. Aquí t'expliquem els senyals més habituals i com actuar-hi:",
        [
          ["1. Llums que parpellegen o fluctuen", ["Causes: connexions soltes, sobrecàrrega del circuit, problemes al transformador", "Quan preocupar-se: si passa sovint o afecta diversos llums", "Solucions: revisió professional del cablejat i les connexions"]],
          ["2. Endolls que fan espurnes", ["Senyals de perill: espurnes blaves o grogues, marques de cremat", "Mesures preventives: no sobrecarregar els endolls, mantenir allunyats els materials inflamables", "Quan trucar: immediatament si les espurnes són freqüents o fa olor de cremat"]],
          ["3. Diferencials o magnetotèrmics que salten sovint", ["Motius: sobrecàrrega del circuit, curtcircuit, aparells defectuosos", "Diagnòstic: identificar quins aparells causen el problema", "Solucions: redistribució de càrregues, actualització del quadre elèctric"]],
          ["4. Olor de cremat a prop dels endolls", ["Identificació: localitzar l'origen exacte de l'olor", "Accions immediates: desconnectar els aparells i tallar el corrent", "Prevenció: inspecció regular d'endolls i connexions"]],
          ["5. Cables desgastats o danyats", ["Inspecció visual: buscar esquerdes, desgast o cablejat exposat", "Riscos: curtcircuits, incendis, descàrregues elèctriques", "Manteniment: substitució immediata dels cables danyats"]],
        ],
        "És fonamental fer inspeccions regulars i contactar amb un electricista professional davant de qualsevol senyal d'alarma. La prevenció i el manteniment són clau per a la seguretat elèctrica a casa."
      ),
    },
    en: {
      title: "How to Spot Electrical Problems at Home Before They Become Serious",
      excerpt: "Learn to recognise the warning signs of electrical problems in your home and avoid dangerous situations.",
      imageAlt: "Electrician inspecting a fuse box wearing safety equipment",
      tags: ["electrical safety", "electrical maintenance", "electrical problems", "electrical installation"],
      content: buildContent(
        "Electrical problems can be dangerous if they are not detected in time. Here are the most common warning signs and what to do about them:",
        [
          ["1. Flickering or fluctuating lights", ["Causes: loose connections, circuit overload, transformer problems", "When to worry: if it happens often or affects several lights", "Solutions: a professional check of the wiring and connections"]],
          ["2. Sparking sockets", ["Danger signs: blue or yellow sparks, scorch marks", "Prevention: do not overload sockets and keep flammable materials away", "When to call: immediately if sparks are frequent or there is a burning smell"]],
          ["3. Breakers that trip frequently", ["Reasons: circuit overload, short circuit, faulty appliances", "Diagnosis: identify which appliances cause the problem", "Solutions: redistributing loads, upgrading the fuse box"]],
          ["4. Burning smells near sockets", ["Identification: find the exact source of the smell", "Immediate action: unplug appliances and switch off the power", "Prevention: regular inspection of sockets and connections"]],
          ["5. Worn or damaged cables", ["Visual inspection: look for cracks, wear or exposed wiring", "Risks: short circuits, fires, electric shocks", "Maintenance: replace damaged cables immediately"]],
        ],
        "Regular inspections are essential, and you should contact a professional electrician at the first warning sign. Prevention and maintenance are key to electrical safety at home."
      ),
    },
    fr: {
      title: "Comment détecter les problèmes électriques chez vous avant qu'ils ne deviennent graves",
      excerpt: "Apprenez à reconnaître les signes avant-coureurs de problèmes électriques chez vous et évitez les situations dangereuses.",
      imageAlt: "Électricien inspectant un tableau électrique avec un équipement de sécurité",
      tags: ["sécurité électrique", "maintenance électrique", "problèmes électriques", "installation électrique"],
      content: buildContent(
        "Les problèmes électriques peuvent être dangereux s'ils ne sont pas détectés à temps. Voici les signes les plus courants et comment réagir :",
        [
          ["1. Lumières qui clignotent ou vacillent", ["Causes : connexions desserrées, surcharge du circuit, problèmes de transformateur", "Quand s'inquiéter : si cela arrive souvent ou touche plusieurs lumières", "Solutions : vérification professionnelle du câblage et des connexions"]],
          ["2. Prises qui font des étincelles", ["Signes de danger : étincelles bleues ou jaunes, traces de brûlure", "Prévention : ne pas surcharger les prises, éloigner les matériaux inflammables", "Quand appeler : immédiatement si les étincelles sont fréquentes ou en cas d'odeur de brûlé"]],
          ["3. Disjoncteurs qui sautent souvent", ["Raisons : surcharge du circuit, court-circuit, appareils défectueux", "Diagnostic : identifier les appareils à l'origine du problème", "Solutions : répartition des charges, mise à niveau du tableau électrique"]],
          ["4. Odeur de brûlé près des prises", ["Identification : localiser la source exacte de l'odeur", "Actions immédiates : débrancher les appareils et couper le courant", "Prévention : inspection régulière des prises et des connexions"]],
          ["5. Câbles usés ou endommagés", ["Inspection visuelle : rechercher fissures, usure ou fils à nu", "Risques : courts-circuits, incendies, électrocutions", "Entretien : remplacement immédiat des câbles endommagés"]],
        ],
        "Il est essentiel de réaliser des inspections régulières et de contacter un électricien professionnel au moindre signe d'alerte. La prévention et l'entretien sont la clé de la sécurité électrique à la maison."
      ),
    },
  },

  'guia-certificado-instalacion-electrica-cie': {
    ca: {
      title: "Guia completa del Certificat d'Instal·lació Elèctrica (CIE)",
      excerpt: "Tot el que has de saber sobre el Certificat d'Instal·lació Elèctrica (CIE): requisits, procés i documentació necessària.",
      imageAlt: "Documents oficials i plànols elèctrics sobre una taula",
      tags: ["certificat CIE", "normativa elèctrica", "instal·lació elèctrica", "documentació"],
      content: buildContent(
        "El Certificat d'Instal·lació Elèctrica (CIE) és un document essencial que garanteix la seguretat de la teva instal·lació elèctrica. Aquesta guia t'ajudarà a entendre tot el que cal:",
        [
          ["1. Què és el CIE?", ["Document oficial obligatori", "Garantia de compliment normatiu", "Validesa legal de la instal·lació"]],
          ["2. Quan es necessita?", ["Instal·lacions noves", "Modificacions importants", "Canvis de potència", "Compravenda d'immobles"]],
          ["3. Procés d'obtenció", ["Inspecció tècnica inicial", "Verificació de la instal·lació", "Documentació necessària", "Tràmits administratius"]],
          ["4. Requisits tècnics", ["Normativa aplicable", "Especificacions tècniques", "Mesures de seguretat", "Proteccions necessàries"]],
          ["5. Validesa i renovació", ["Període de vigència", "Inspeccions periòdiques", "Procés de renovació", "Costos aproximats"]],
        ],
        "L'obtenció del CIE és un procés que requereix la intervenció de professionals qualificats i el compliment estricte de la normativa vigent."
      ),
    },
    en: {
      title: "Complete Guide to the Electrical Installation Certificate (CIE)",
      excerpt: "Everything you need to know about the Electrical Installation Certificate (CIE): requirements, process and necessary documents.",
      imageAlt: "Official documents and electrical plans on a table",
      tags: ["CIE certificate", "electrical regulations", "electrical installation", "documentation"],
      content: buildContent(
        "The Electrical Installation Certificate (CIE, Certificado de Instalación Eléctrica) is an essential document that guarantees the safety of your electrical installation. This guide will help you understand everything you need:",
        [
          ["1. What is the CIE?", ["Mandatory official document", "Proof of regulatory compliance", "Legal validity of the installation"]],
          ["2. When is it needed?", ["New installations", "Major modifications", "Changes to contracted power", "Buying or selling a property"]],
          ["3. How to obtain it", ["Initial technical inspection", "Verification of the installation", "Required documents", "Administrative procedures"]],
          ["4. Technical requirements", ["Applicable regulations", "Technical specifications", "Safety measures", "Required protections"]],
          ["5. Validity and renewal", ["Period of validity", "Periodic inspections", "Renewal process", "Approximate costs"]],
        ],
        "Obtaining the CIE requires the involvement of qualified professionals and strict compliance with current regulations."
      ),
    },
    fr: {
      title: "Guide complet du Certificat d'Installation Électrique (CIE)",
      excerpt: "Tout ce qu'il faut savoir sur le Certificat d'Installation Électrique (CIE) : exigences, démarches et documents nécessaires.",
      imageAlt: "Documents officiels et plans électriques sur une table",
      tags: ["certificat CIE", "réglementation électrique", "installation électrique", "documentation"],
      content: buildContent(
        "Le Certificat d'Installation Électrique (CIE, Certificado de Instalación Eléctrica) est un document essentiel qui garantit la sécurité de votre installation électrique. Ce guide vous aidera à tout comprendre :",
        [
          ["1. Qu'est-ce que le CIE ?", ["Document officiel obligatoire", "Garantie de conformité réglementaire", "Validité légale de l'installation"]],
          ["2. Quand est-il nécessaire ?", ["Nouvelles installations", "Modifications importantes", "Changements de puissance", "Achat ou vente d'un bien immobilier"]],
          ["3. Démarches d'obtention", ["Inspection technique initiale", "Vérification de l'installation", "Documents nécessaires", "Démarches administratives"]],
          ["4. Exigences techniques", ["Réglementation applicable", "Spécifications techniques", "Mesures de sécurité", "Protections nécessaires"]],
          ["5. Validité et renouvellement", ["Durée de validité", "Inspections périodiques", "Procédure de renouvellement", "Coûts approximatifs"]],
        ],
        "L'obtention du CIE nécessite l'intervention de professionnels qualifiés et le strict respect de la réglementation en vigueur."
      ),
    },
  },

  'instalacion-puntos-recarga-vehiculos-electricos': {
    ca: {
      title: "Instal·lació de punts de recàrrega per a vehicles elèctrics",
      excerpt: "Guia definitiva sobre la instal·lació de punts de recàrrega en comunitats i habitatges: normativa, procés i subvencions.",
      imageAlt: "Carregador connectat a un cotxe elèctric",
      tags: ["vehicle elèctric", "punt de recàrrega", "instal·lació elèctrica", "mobilitat sostenible"],
      content: buildContent(
        "La instal·lació de punts de recàrrega per a vehicles elèctrics és cada vegada més demandada. Aquesta guia t'ajudarà a entendre tots els aspectes importants:",
        [
          ["1. Tipus de punts de recàrrega", ["Càrrega lenta (fins a 7,4 kW)", "Càrrega semiràpida (fins a 22 kW)", "Càrrega ràpida (50 kW o més)", "Avantatges i inconvenients de cada tipus"]],
          ["2. Requisits tècnics", ["Potència necessària", "Tipus d'instal·lació", "Proteccions elèctriques", "Connectors disponibles"]],
          ["3. Procés d'instal·lació", ["Estudi previ", "Tràmits administratius", "Instal·lació física", "Certificació i posada en marxa"]],
          ["4. Normativa aplicable", ["Reglament electrotècnic", "Normatives específiques", "Requisits de seguretat", "Certificacions necessàries"]],
          ["5. Subvencions disponibles", ["Ajuts estatals", "Ajuts autonòmics", "Requisits per sol·licitar-los", "Procés de tramitació"]],
        ],
        "La instal·lació l'han de fer professionals qualificats per garantir la seguretat i l'eficiència del sistema."
      ),
    },
    en: {
      title: "Installing Electric Vehicle Charging Points",
      excerpt: "The definitive guide to installing charging points in residential communities and homes: regulations, process and grants.",
      imageAlt: "Charger plugged into an electric car",
      tags: ["electric vehicle", "charging point", "electrical installation", "sustainable mobility"],
      content: buildContent(
        "Demand for electric vehicle charging points keeps growing. This guide will help you understand all the important aspects:",
        [
          ["1. Types of charging points", ["Slow charging (up to 7.4 kW)", "Semi-fast charging (up to 22 kW)", "Fast charging (50 kW or more)", "Pros and cons of each type"]],
          ["2. Technical requirements", ["Required power", "Type of installation", "Electrical protections", "Available connectors"]],
          ["3. Installation process", ["Preliminary study", "Administrative procedures", "Physical installation", "Certification and commissioning"]],
          ["4. Applicable regulations", ["Low Voltage Electrotechnical Regulation (REBT)", "Specific regulations", "Safety requirements", "Required certifications"]],
          ["5. Available grants", ["State grants", "Regional grants", "Eligibility requirements", "Application process"]],
        ],
        "The installation must be carried out by qualified professionals to guarantee the safety and efficiency of the system."
      ),
    },
    fr: {
      title: "Installation de bornes de recharge pour véhicules électriques",
      excerpt: "Le guide complet pour installer des bornes de recharge en copropriété et en maison individuelle : réglementation, démarches et aides.",
      imageAlt: "Chargeur branché sur une voiture électrique",
      tags: ["véhicule électrique", "borne de recharge", "installation électrique", "mobilité durable"],
      content: buildContent(
        "L'installation de bornes de recharge pour véhicules électriques est de plus en plus demandée. Ce guide vous aidera à comprendre tous les points importants :",
        [
          ["1. Types de bornes de recharge", ["Recharge lente (jusqu'à 7,4 kW)", "Recharge semi-rapide (jusqu'à 22 kW)", "Recharge rapide (50 kW ou plus)", "Avantages et inconvénients de chaque type"]],
          ["2. Exigences techniques", ["Puissance nécessaire", "Type d'installation", "Protections électriques", "Connecteurs disponibles"]],
          ["3. Processus d'installation", ["Étude préalable", "Démarches administratives", "Installation physique", "Certification et mise en service"]],
          ["4. Réglementation applicable", ["Règlement électrotechnique basse tension (REBT)", "Réglementations spécifiques", "Exigences de sécurité", "Certifications nécessaires"]],
          ["5. Aides disponibles", ["Aides de l'État", "Aides régionales", "Conditions d'éligibilité", "Démarches de demande"]],
        ],
        "L'installation doit être réalisée par des professionnels qualifiés afin de garantir la sécurité et l'efficacité du système."
      ),
    },
  },

  'ahorro-energetico-reducir-factura-luz': {
    ca: {
      title: "Estalvi energètic: com reduir la factura de la llum",
      excerpt: "Consells pràctics i solucions efectives per reduir el consum elèctric i estalviar en la factura mensual.",
      imageAlt: "Bombeta LED moderna en un entorn domèstic",
      tags: ["estalvi energètic", "eficiència energètica", "factura de la llum", "consum elèctric"],
      content: buildContent(
        "Reduir el consum elèctric no només beneficia la teva butxaca, sinó també el medi ambient. Aquí t'expliquem com aconseguir-ho:",
        [
          ["1. Il·luminació eficient", ["Canvi a tecnologia LED", "Aprofitament de la llum natural", "Sensors de moviment", "Temporitzadors"]],
          ["2. Electrodomèstics eficients", ["Classificació energètica", "Ús òptim", "Manteniment adequat", "Renovació d'equips antics"]],
          ["3. Climatització intel·ligent", ["Aïllament tèrmic", "Termòstats programables", "Ús eficient", "Manteniment preventiu"]],
          ["4. Hàbits de consum", ["Hores vall", "Desconnexió del mode d'espera", "Ús conscient", "Monitoratge del consum"]],
          ["5. Millores a la instal·lació", ["Revisió del contracte", "Optimització de la potència", "Correcció del factor de potència", "Manteniment preventiu"]],
        ],
        "Aplicar aquestes mesures pot suposar un estalvi significatiu en la factura mensual."
      ),
    },
    en: {
      title: "Energy Saving: How to Lower Your Electricity Bill",
      excerpt: "Practical tips and effective solutions to cut your electricity consumption and save on your monthly bill.",
      imageAlt: "Modern LED bulb in a home setting",
      tags: ["energy saving", "energy efficiency", "electricity bill", "electricity consumption"],
      content: buildContent(
        "Reducing your electricity consumption is good for your wallet and for the environment. Here is how to do it:",
        [
          ["1. Efficient lighting", ["Switching to LED technology", "Making the most of natural light", "Motion sensors", "Timers"]],
          ["2. Efficient appliances", ["Energy rating", "Optimal use", "Proper maintenance", "Replacing old appliances"]],
          ["3. Smart heating and cooling", ["Thermal insulation", "Programmable thermostats", "Efficient use", "Preventive maintenance"]],
          ["4. Consumption habits", ["Off-peak hours", "Switching off standby", "Mindful use", "Monitoring consumption"]],
          ["5. Improvements to your installation", ["Reviewing your contract", "Optimising contracted power", "Power factor correction", "Preventive maintenance"]],
        ],
        "Putting these measures into practice can mean significant savings on your monthly bill."
      ),
    },
    fr: {
      title: "Économies d'énergie : comment réduire votre facture d'électricité",
      excerpt: "Conseils pratiques et solutions efficaces pour réduire votre consommation électrique et économiser sur votre facture mensuelle.",
      imageAlt: "Ampoule LED moderne dans un intérieur",
      tags: ["économies d'énergie", "efficacité énergétique", "facture d'électricité", "consommation électrique"],
      content: buildContent(
        "Réduire sa consommation électrique profite à votre portefeuille, mais aussi à l'environnement. Voici comment y parvenir :",
        [
          ["1. Éclairage efficace", ["Passage à la technologie LED", "Utilisation de la lumière naturelle", "Détecteurs de mouvement", "Minuteries"]],
          ["2. Appareils électroménagers efficaces", ["Classe énergétique", "Utilisation optimale", "Entretien adapté", "Remplacement des appareils anciens"]],
          ["3. Climatisation et chauffage intelligents", ["Isolation thermique", "Thermostats programmables", "Utilisation efficace", "Entretien préventif"]],
          ["4. Habitudes de consommation", ["Heures creuses", "Extinction des appareils en veille", "Consommation raisonnée", "Suivi de la consommation"]],
          ["5. Améliorations de l'installation", ["Révision du contrat", "Optimisation de la puissance", "Correction du facteur de puissance", "Entretien préventif"]],
        ],
        "Mettre en place ces mesures peut représenter des économies importantes sur votre facture mensuelle."
      ),
    },
  },

  'seguridad-electrica-reformas': {
    ca: {
      title: "Seguretat elèctrica en reformes: el que has de saber",
      excerpt: "Aspectes fonamentals de seguretat elèctrica que cal tenir en compte durant la reforma del teu habitatge.",
      imageAlt: "Electricista treballant en una reforma amb equip de seguretat",
      tags: ["reforma", "seguretat elèctrica", "normativa", "instal·lació elèctrica"],
      content: buildContent(
        "La seguretat elèctrica és crucial en qualsevol reforma. Aquí t'expliquem els aspectes més importants:",
        [
          ["1. Normativa vigent", ["Reglament electrotècnic", "Normatives locals", "Certificacions necessàries", "Documentació requerida"]],
          ["2. Elements de seguretat", ["Proteccions elèctriques", "Preses de terra", "Diferencials", "Magnetotèrmics"]],
          ["3. Instal·lació segura", ["Dimensionament correcte", "Materials homologats", "Connexions adequades", "Canalitzacions"]],
          ["4. Professionals qualificats", ["Electricistes autoritzats", "Certificacions necessàries", "Experiència demostrable", "Assegurances de responsabilitat"]],
          ["5. Manteniment posterior", ["Revisions periòdiques", "Documentació actualitzada", "Modificacions segures", "Registre d'intervencions"]],
        ],
        "La seguretat elèctrica no és negociable en una reforma."
      ),
    },
    en: {
      title: "Electrical Safety in Renovations: What You Need to Know",
      excerpt: "Key electrical safety points to keep in mind when renovating your home.",
      imageAlt: "Electrician working on a renovation wearing safety equipment",
      tags: ["renovation", "electrical safety", "regulations", "electrical installation"],
      content: buildContent(
        "Electrical safety is crucial in any renovation. Here are the most important aspects:",
        [
          ["1. Current regulations", ["Electrotechnical regulation", "Local regulations", "Required certifications", "Required documentation"]],
          ["2. Safety components", ["Electrical protections", "Earthing", "Residual current devices (RCDs)", "Circuit breakers"]],
          ["3. Safe installation", ["Correct sizing", "Approved materials", "Proper connections", "Conduits and cable routing"]],
          ["4. Qualified professionals", ["Authorised electricians", "Required certifications", "Proven experience", "Liability insurance"]],
          ["5. Ongoing maintenance", ["Periodic inspections", "Up-to-date documentation", "Safe modifications", "Record of interventions"]],
        ],
        "Electrical safety is not negotiable in a renovation."
      ),
    },
    fr: {
      title: "Sécurité électrique lors de travaux de rénovation : ce qu'il faut savoir",
      excerpt: "Les points essentiels de sécurité électrique à prendre en compte lors de la rénovation de votre logement.",
      imageAlt: "Électricien travaillant sur un chantier de rénovation avec un équipement de sécurité",
      tags: ["rénovation", "sécurité électrique", "réglementation", "installation électrique"],
      content: buildContent(
        "La sécurité électrique est primordiale dans toute rénovation. Voici les points les plus importants :",
        [
          ["1. Réglementation en vigueur", ["Règlement électrotechnique", "Réglementations locales", "Certifications nécessaires", "Documents requis"]],
          ["2. Éléments de sécurité", ["Protections électriques", "Mise à la terre", "Interrupteurs différentiels", "Disjoncteurs magnétothermiques"]],
          ["3. Installation sûre", ["Dimensionnement correct", "Matériaux homologués", "Connexions adaptées", "Canalisations"]],
          ["4. Professionnels qualifiés", ["Électriciens agréés", "Certifications nécessaires", "Expérience avérée", "Assurance responsabilité civile"]],
          ["5. Entretien ultérieur", ["Contrôles périodiques", "Documentation à jour", "Modifications sécurisées", "Registre des interventions"]],
        ],
        "La sécurité électrique n'est pas négociable lors d'une rénovation."
      ),
    },
  },
};

/** Textos de la interfaz del blog en cada idioma */
export const blogUi: Record<Lang, {
  heading: string;
  seoTitle: string;
  seoDescription: string;
  loading: string;
  empty: string;
  back: string;
  notFound: string;
  loadingPost: string;
}> = {
  es: {
    heading: "Blog de Electricidad y Hogar",
    seoTitle: "Blog de Electricidad | Consejos y Guías del Experto",
    seoDescription: "Artículos profesionales sobre instalaciones eléctricas, seguridad, mantenimiento y consejos prácticos. Todo lo que necesitas saber sobre electricidad en el hogar.",
    loading: "Cargando artículos...",
    empty: "No hay artículos publicados todavía.",
    back: "Volver al blog",
    notFound: "Artículo no encontrado",
    loadingPost: "Cargando artículo...",
  },
  ca: {
    heading: "Blog d'electricitat i llar",
    seoTitle: "Blog d'electricitat | Consells i guies de l'expert",
    seoDescription: "Articles professionals sobre instal·lacions elèctriques, seguretat, manteniment i consells pràctics. Tot el que has de saber sobre l'electricitat a casa.",
    loading: "Carregant articles...",
    empty: "Encara no hi ha articles publicats.",
    back: "Tornar al blog",
    notFound: "Article no trobat",
    loadingPost: "Carregant l'article...",
  },
  en: {
    heading: "Electricity & Home Blog",
    seoTitle: "Electricity Blog | Expert Tips and Guides",
    seoDescription: "Professional articles on electrical installations, safety, maintenance and practical tips. Everything you need to know about electricity at home.",
    loading: "Loading articles...",
    empty: "No articles published yet.",
    back: "Back to blog",
    notFound: "Article not found",
    loadingPost: "Loading article...",
  },
  fr: {
    heading: "Blog électricité et maison",
    seoTitle: "Blog électricité | Conseils et guides d'expert",
    seoDescription: "Articles professionnels sur les installations électriques, la sécurité, l'entretien et des conseils pratiques. Tout ce qu'il faut savoir sur l'électricité à la maison.",
    loading: "Chargement des articles...",
    empty: "Aucun article publié pour le moment.",
    back: "Retour au blog",
    notFound: "Article introuvable",
    loadingPost: "Chargement de l'article...",
  },
};

/** Imágenes de Unsplash que han dejado de existir -> sustituta */
const IMAGE_FIXES: Record<string, string> = {
  'photo-1558425281-c539e5e5f28f': 'photo-1593941707882-a5bba14938c7',
  'photo-1621905252189-08b45d6a269e': 'photo-1621905251189-08b45d6a269e',
};

export const fixImage = (url?: string | null): string => {
  if (!url) return '';
  for (const [broken, ok] of Object.entries(IMAGE_FIXES)) {
    if (url.includes(broken)) return url.replace(broken, ok);
  }
  return url;
};

/** Devuelve el artículo en el idioma pedido (si hay traducción) y con la imagen corregida */
export function localizePost<T extends { slug: string; image: string }>(post: T, lang: Lang): T {
  const tr = lang === 'es' ? undefined : blogTranslations[post.slug]?.[lang];
  const base: T = { ...post, image: (blogImages as Record<string, string>)[post.slug] || fixImage(post.image) };
  return tr ? { ...base, ...tr } : base;
}

// Traducciones de los artículos automáticos (scripts/blog-diario.mjs)
import autoPostsTr from './autoPosts.json';
for (const p of autoPostsTr as any[]) {
  const { es: _es, ...rest } = p.i18n;
  (blogTranslations as any)[p.slug] = rest;
}
